import {Grid, Tooltip} from "@mui/material";
import React from "react";
import {AttributeInput} from "./AttributeInput";
import {ProductDetail} from "./ProductDetail";
import EnhancedTextField from "../../../../../components/EnhancedTextField";
import EnhancedSelect from "../../../../../components/EnhancedSelect";
import {AttributeDiffWithInput} from "./AttributeDiff";
import {useDidUpdateEffect, AI_ASSIST_FEATURES} from "../../../../../utils/utils";
import {AISparklesIcon} from '../../../../../components/AIStarsIcon';
import {isStrictlyRequired} from "../../import/product/components/AttributeMapping";
import type {
    CatalogProduct,
    ProductAttribute,
    ProductCategory,
} from "../types";

// region TypeScript Interfaces
interface VariesByOption {
    label: React.ReactNode;
    value: string;
    type?: string;
}

export interface DisplayAttributeProps {
    index: number;
    colSpan: number;
    attribute: ProductAttribute;
    value: any;
    compareValue: any;
    disabled?: boolean;
    errors: Record<string, string>;
    onChange: (attributeName: string, value: any) => void;
    onAIEnrich?: (attributeName: string) => void;
    retailerProduct: CatalogProduct | null;
    forceRefresh: boolean;
    setForceRefresh: (val: boolean) => void;
    filteredVariesByOptions?: VariesByOption[];
    filteredVariesByFields?: VariesByOption[];
    partnerId?: string | number;
    isRetailer?: boolean;
    catalog?: string;
    currentCatalog?: string;
}

export interface DisplayKvpListAttributeProps {
    index: number;
    attribute: ProductAttribute;
    product: CatalogProduct;
    errors: Record<string, string>;
    disabled?: boolean;
    onChange: (attributeName: string, value: any) => void;
    onAIEnrich?: (attributeName: string) => void;
    retailerProduct: CatalogProduct | null;
    forceRefresh: boolean;
    setForceRefresh: (val: boolean) => void;
    filteredVariesByOptions?: VariesByOption[];
    filteredVariesByFields?: VariesByOption[];
    partnerId?: string | number;
    isRetailer?: boolean;
    catalog?: string;
    currentCatalog?: string;
}

export interface AttributeGroupProps {
    group: ProductAttribute[];
    name: string;
    errors: Record<string, string>;
    disabled?: boolean;
    index: number;
    onChange: (attributeName: string, value: any) => void;
    productId?: string | undefined;
    product: CatalogProduct;
    retailerProduct: CatalogProduct | null;
    availableCategories: ProductCategory[];
    filteredVariesByOptions: VariesByOption[];
    filteredVariesByFields: VariesByOption[];
    availableAttributes: ProductAttribute[];
    refreshing?: boolean;
    aiApplyCounter?: number;
    onAIEnrich?: (attributeName: string) => void;
    partnerId?: string | number;
    isRetailer?: boolean;
    catalog?: string;
    currentCatalog?: string;
}
// endregion

const manuallyRenderedComponents = ["parent_sku", "created_date", "variant_name", "listed_by", "listed_date", "delisted_by", "delisted_date", "approved_by", "approval_date", "tags", "variant_fields", "varies_by", "approval_status", "sync_status", "category", "name", "attribute_set", "sku", "image_metadata", "public_note", "private_note"];
const manuallyRenderedComponentTypes = ["image"];

export const DisplayKvpListAttribute = (props: DisplayKvpListAttributeProps) => {
    const {index, attribute, product, errors, disabled, onChange, retailerProduct, forceRefresh, setForceRefresh, filteredVariesByOptions, filteredVariesByFields} = props;

    const colSpan = 6;
    const name = attribute.Name;
    const value = product.Attributes[name];

    if (!value) return <></>;
    if (Array.isArray(value)) {
        const recombineOnChange = (itemName: string, itemValue: any, itemIndex: number) => {
            let newValue: any[] = [];
            value.forEach((item: any, idx: number) => {
                if (item.name === itemName && idx === itemIndex)
                    newValue.push({name: itemName, value: itemValue});
                else
                    newValue.push({...item});
            });

            onChange(name, newValue);
        }

        return (product.Attributes[attribute.Name] as any[]).map((item: any, idx: number) => {
            const comparedRetailerProduct = retailerProduct?.Attributes[attribute.Name];
            const comparedRetailerProductItem = comparedRetailerProduct && comparedRetailerProduct[idx];

            return (
                <DisplayAttribute key={idx} value={item.value}
                                  compareValue={retailerProduct && comparedRetailerProductItem?.value}
                                  disabled={disabled}
                                  errors={errors}
                                  onChange={(_attributeName: string, value: any) => {
                                      recombineOnChange(item.name, value, idx);
                                  }}
                                  onAIEnrich={props.onAIEnrich}
                                  retailerProduct={retailerProduct}
                                  forceRefresh={forceRefresh} setForceRefresh={setForceRefresh}
                                  index={idx} colSpan={colSpan}
                                  attribute={{
                                      Name: attribute.Name,
                                      FriendlyName: attribute.FriendlyName + ": " + item.name,
                                      Type: "text"
                                  }}
                                  filteredVariesByOptions={filteredVariesByOptions}
                                  filteredVariesByFields={filteredVariesByFields}
                                  partnerId={props.partnerId}
                                  isRetailer={props.isRetailer}
                                  catalog={props.catalog}
                                  currentCatalog={props.currentCatalog}
                />
            );
        }) as any;
    }
    return (
        <DisplayAttribute key={index} value={value}
                          compareValue={retailerProduct && (retailerProduct.Attributes[attribute.Name] as any)?.value}
                          disabled={disabled}
                          errors={errors} onChange={onChange}
                          onAIEnrich={props.onAIEnrich}
                          retailerProduct={retailerProduct}
                          forceRefresh={forceRefresh} setForceRefresh={setForceRefresh}
                          index={index} colSpan={colSpan}
                          attribute={{
                              Name: attribute.Name,
                              FriendlyName: attribute.FriendlyName,
                              Type: "text"
                          }}
                          filteredVariesByOptions={filteredVariesByOptions}
                          filteredVariesByFields={filteredVariesByFields}
                          partnerId={props.partnerId}
                          isRetailer={props.isRetailer}
                          catalog={props.catalog}
                          currentCatalog={props.currentCatalog}
        />
    )
}

export const DisplayAttribute = (props: DisplayAttributeProps) => {
    const {attribute, value, disabled, errors, onChange, onAIEnrich, retailerProduct, forceRefresh, setForceRefresh} = props;
    const {compareValue} = props;
    const {index, colSpan} = props;
    const {filteredVariesByOptions = [], filteredVariesByFields = []} = props;

    const isVariesBy = filteredVariesByOptions.some(option => option.value === attribute.Name);
    const isVariant = filteredVariesByFields.some(field => field.value === attribute.Name);

    return (
        <Grid key={index} container size={colSpan} justifyContent={"flex-start"} alignItems={"center"}
              style={{padding: "6px"}} item={true}>
            <AttributeDiffWithInput
                disabled={disabled || props.currentCatalog === "mycatalog" || retailerProduct == null}
                value={value}
                compare={compareValue}
                rejectChange={() => {
                    onChange(attribute.Name, compareValue);
                    setForceRefresh(!forceRefresh);
                }}
                field={(
                    <AttributeInput
                        forceRefresh={forceRefresh}
                        labelstyles={disabled || props.currentCatalog === "mycatalog" || retailerProduct == null ? {} : {
                            pointerEvents: "auto !important" as any,
                            cursor: "pointer !important" as any,
                            height: "16px",
                            display: "flex",
                        }}
                        label={(
                            <>
                                {attribute.FriendlyName}
                                {(isVariesBy || isVariant) && (
                                    <span style={{ 
                                        display: "inline-block",
                                        borderRadius: "10px",
                                        backgroundColor: isVariesBy ? "#a1c593" : "#9c9cb7",
                                        color: "#202020",
                                        fontSize: "0.75em",
                                        padding: "0 6px",
                                        margin: "0 4px",
                                        verticalAlign: "middle",
                                        lineHeight: "1.25",
                                    }}>
                                        {isVariesBy ? "Varies By" : "Variant Field"}
                                    </span>
                                )}
                            </>
                        )}
                        attribute={attribute} index={index} value={value} disabled={disabled}
                        onChange={onChange}
                        onAIEnrich={onAIEnrich}
                        error={errors[attribute.Name] == null ? "" : errors[attribute.Name]}
                        partnerId={props.partnerId}
                        isRetailer={props.isRetailer}
                        catalog={props.catalog}
                    />
                )}
            />
        </Grid>
    );
}

export const cleanGroupAttributes = (group: ProductAttribute[], product: CatalogProduct): ProductAttribute[] => {
    // Remove Certain Attributes From Groups //
    let cleanedGroup = group.filter((attribute) => {
        if (manuallyRenderedComponents.includes(attribute.Name)
            || manuallyRenderedComponentTypes.includes(attribute.Type as string)
            || attribute.Hidden)
            return false;

        // Special Case: check empty KVP lists //
        if (attribute.Type === "kvplist" && !product.Attributes[attribute.Name]) return false;

        return true;
    });

    return [...cleanedGroup];
}

export const AttributeGroup = (props: AttributeGroupProps) => {
    const fullSpanEditorTypes = ["textarea", "html"];
    const {group, name, errors, disabled, index, onChange} = props;
    const {productId, product, retailerProduct} = props;
    const {availableCategories, filteredVariesByOptions, filteredVariesByFields, availableAttributes} = props;

    const [forceRefresh, setForceRefresh] = React.useState(false);

    const hasVariantName = availableAttributes.find(attr => attr.Name === "variant_name");

    const isSystemAttributeRequired = (attributeName: string): boolean => {
        const attr = availableAttributes.find(a => a.Name === attributeName);
        return attr ? isStrictlyRequired(attr.ValidationRules) : false;
    };

    useDidUpdateEffect(() => {
        setForceRefresh(prev => !prev);
    }, [props.refreshing]);

    // AI enrichment apply: bumps a counter on the parent so AttributeInput
    // localValue resyncs even though its memo comparator skips re-render for
    // text/video/document fields. Without this, accepting a suggestion saves
    // the new value but the visible input stays on the old value.
    useDidUpdateEffect(() => {
        setForceRefresh(prev => !prev);
    }, [props.aiApplyCounter]);

    // Parse display name from format: {ORDER}.{Name of the display group}
    const getDisplayName = (groupName: string): string => {
        const match = groupName.match(/^(\d+)\.(.+)$/);
        return match ? match[2].trim() : groupName;
    };

    const displayName = getDisplayName(name);

    return (
        <ProductDetail key={index} listkey={index} id={name} title={displayName} content={
            <>
                {/*Special Render Cases*/}
                {(displayName === "Basics" || name === "Basics") && (
                    <>
                        <Grid item container size={12} style={{padding: "6px"}}>
                            <AttributeDiffWithInput
                                disabled={disabled || props.currentCatalog === "mycatalog" || retailerProduct == null}
                                value={product.Attributes["name"]}
                                compare={retailerProduct && retailerProduct.Attributes["name"]}
                                rejectChange={() => onChange("name", retailerProduct!.Attributes["name"])}
                                field={(
                                    <EnhancedTextField
                                        labelstyles={(disabled || props.currentCatalog === "mycatalog" || retailerProduct == null ? {} : {
                                            pointerEvents: "auto !important",
                                            cursor: "pointer !important",
                                            height: "16px"
                                        }) as React.CSSProperties}
                                        label={(
                                            <>
                                                {"Name"}
                                                {(AI_ASSIST_FEATURES() && !disabled && props.onAIEnrich) && (
                                                    <>
                                                        {isSystemAttributeRequired("name") && (
                                                            <span style={{ color: '#f44336' }}>&thinsp;*</span>
                                                        )}
                                                        <Tooltip title="Use AI to generate Name">
                                                            <span
                                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); props.onAIEnrich!("name"); }}
                                                                style={{ cursor: 'pointer', display: 'inline-flex', verticalAlign: 'middle', marginLeft: '4px', marginTop: '-2px', marginBottom: '-2px', pointerEvents: 'auto' }}
                                                            >
                                                                <AISparklesIcon size={20} />
                                                            </span>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </>
                                        )}
                                        value={product.Attributes["name"]} disabled={disabled}
                                        inputProps={{"data-testid": "poc-product-name-input"}}
                                        required={(AI_ASSIST_FEATURES() && !disabled && props.onAIEnrich) ? false : isSystemAttributeRequired("name")}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange("name", e.target.value)}/>
                                )}
                            />
                        </Grid>

                        <Grid item container size={6} style={{padding: "6px"}}>
                            <EnhancedTextField
                                label={"Parent SKU"} value={product.Attributes["parent_sku"]}
                                inputProps={{"data-testid": "poc-product-parent-sku-input"}}
                                required={isSystemAttributeRequired("parent_sku")}
                                error={errors.parent_sku} helperText={errors.parent_sku}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange("parent_sku", e.target.value)}/>
                        </Grid>

                        <Grid item container size={6} style={{padding: "6px"}}>
                        <AttributeDiffWithInput
                            disabled={disabled || props.currentCatalog === "mycatalog" || retailerProduct == null}
                            value={product.Attributes["category"]}
                            compare={retailerProduct && retailerProduct.Attributes["category"]}
                            rejectChange={() => onChange("category", retailerProduct!.Attributes["category"])}
                            field={(
                                <EnhancedSelect
                                    label="Category"
                                    isClearable
                                    value={availableCategories.find(cat => cat.Name === product.Attributes["category"]) ? {
                                        value: product.Attributes["category"],
                                        label: product.Attributes["category"],
                                        path: availableCategories.find(cat => cat.Name === product.Attributes["category"])!.Path
                                    } : null}
                                    onChange={(option: any, _actionMeta: any) => {
                                        onChange("category", option ? option.value : '');
                                    }}
                                    options={availableCategories.map(cat => ({
                                        value: cat.Name,
                                        label: cat.Name,
                                        path: cat.Path,
                                    }))}
                                    required={isSystemAttributeRequired("category")}
                                    disabled={disabled}
                                    formatOptionLabel={(option: any) => (
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span>{option.label}</span>
                                            {option.path && <span className={"enhanced-select__option__subtext"} style={{ fontSize: 'small' }}>{option.path}</span>}
                                        </div>
                                    )}
                                    itemHeight={80}
                                />
                            )}
                        />
                        </Grid>

                        <Grid item container size={hasVariantName ? 6 : 12} style={{padding: "6px"}}>
                            <EnhancedTextField
                                label={"Variant SKU"} value={product.Attributes["sku"]} required
                                inputProps={{"data-testid": "poc-product-sku-input"}}
                                error={errors.sku} helperText={errors.sku} disabled={disabled || productId != null}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange("sku", e.target.value)}/>
                        </Grid>

                        {hasVariantName && (
                            <Grid item container size={6} style={{padding: "6px"}}>
                                <EnhancedTextField
                                    label={(
                                        <>
                                            {"Variant Name"}
                                            {(AI_ASSIST_FEATURES() && !disabled && props.onAIEnrich) && (
                                                <>
                                                    {isSystemAttributeRequired("variant_name") && (
                                                        <span style={{ color: '#f44336' }}>&thinsp;*</span>
                                                    )}
                                                    <Tooltip title="Use AI to generate Variant Name">
                                                        <span
                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); props.onAIEnrich!("variant_name"); }}
                                                            style={{ cursor: 'pointer', display: 'inline-flex', verticalAlign: 'middle', marginLeft: '4px', marginTop: '-2px', marginBottom: '-2px', pointerEvents: 'auto' }}
                                                        >
                                                            <AISparklesIcon size={20} />
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </>
                                    )}
                                    value={product.Attributes["variant_name"]}
                                    required={!(AI_ASSIST_FEATURES() && !disabled && props.onAIEnrich) && isSystemAttributeRequired("variant_name")}
                                    error={errors.variant_name} helperText={errors.variant_name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange("variant_name", e.target.value)}/>
                            </Grid>
                        )}
                    </>
                )}

                {/*General Render Case*/}
                {group.map((attribute, idx) => {
                    let value = product.Attributes[attribute.Name];

                    let colSpan = group.length === 1 ? 12 : 6;
                    if (fullSpanEditorTypes.includes(attribute.Editor as string) || attribute.Type === "video" || attribute.Type === "document") colSpan = 12;

                    // KVP List Render, one attribute breaks up into multiple fields //
                    if (attribute.Type === "kvplist") {
                        return (
                            <DisplayKvpListAttribute key={idx} index={idx} attribute={attribute} product={product}
                                                    errors={errors} disabled={disabled} onChange={onChange}
                                                    retailerProduct={retailerProduct} forceRefresh={forceRefresh}
                                                    setForceRefresh={setForceRefresh}
                                                    filteredVariesByOptions={filteredVariesByOptions}
                                                    filteredVariesByFields={filteredVariesByFields}/>
                        );
                    }

                    return (
                        <DisplayAttribute key={idx} attribute={attribute} value={value}
                                          compareValue={retailerProduct && retailerProduct.Attributes[attribute.Name]}
                                          disabled={disabled}
                                          errors={errors} onChange={onChange}
                                          onAIEnrich={props.onAIEnrich} retailerProduct={retailerProduct}
                                          forceRefresh={forceRefresh} setForceRefresh={setForceRefresh}
                                          index={idx} colSpan={colSpan}
                                          filteredVariesByOptions={filteredVariesByOptions}
                                          filteredVariesByFields={filteredVariesByFields}
                                          partnerId={props.partnerId}
                                          isRetailer={props.isRetailer}
                                          catalog={props.catalog}
                                          currentCatalog={props.currentCatalog} />
                    );
                })}
            </>
        }/>
    );
};
